import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import {
  applyContactBookToRooms,
  getContactBooks,
} from "../../slices/communication";
import Card from "../layout/Card";
import { checkArraysContainSameElements } from "../../utils/arrays";
import Dropdown from "../forms/Dropdown";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import Checkbox from "../forms/Checkbox";

export default function Calling() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getContactBooks());
    dispatch(getRooms());
  }, [dispatch]);

  // Set state
  const [selectedContactBook, setSelectedContactBook] = useState<{
    id: string;
    name: string;
    choose_rooms_by: "name" | "tag";
    rooms: string[];
    tags: string[];
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { contact_books: contactBooks } = useAppSelector(
    (state) => state.communication,
  );
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  // Functions
  function selectContactBookToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [contactBookId] = args;

    // Build list of valid rooms based on matching contacts
    if (contactBooks.data === null) return;

    let selectedRooms = [];
    const contactBookData = contactBooks.data.filter(
      (book) => book.id === contactBookId,
    )[0];
    const selectedContactIds = contactBookData.contacts.map(
      (x) => x.contact_id,
    );

    // Iterate through rooms and find matching contacts
    for (let i = 0; i < roomsData.length; i += 1) {
      const room = roomsData[i];
      const roomContactsRooms = room.contacts.rooms.map((x) => x.contact_id);
      let roomContactIds = [...roomContactsRooms];
      const roomContactNumbers = room.contacts.phone_numbers.map(
        (x) => x.contact_id,
      );
      roomContactIds = roomContactIds.concat(roomContactNumbers);
      const check = checkArraysContainSameElements(
        roomContactIds,
        selectedContactIds,
      );
      if (check) {
        selectedRooms.push(room.id);
      }
    }

    setSelectedContactBook({
      id: contactBookData.id,
      name: contactBookData.name,
      choose_rooms_by: "name",
      rooms: selectedRooms,
      tags: [],
    });
    setShowEditModal(true);
  }

  async function confirmEditContactBook(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "warning"),
      );
      return;
    }
    if (selectedContactBook === null) {
      dispatch(
        addAlert(
          generateAlertText("missing_contact_book", language),
          "warning",
        ),
      );
      return;
    }

    const {
      id,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
    } = selectedContactBook;

    // Build and validate rooms array
    let contactBookRooms = rooms;
    if (chooseRoomsBy === "tag") {
      if (tags.length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_tags", language), "danger", 5000),
        );
        return;
      }
      contactBookRooms = [];
      for (let i = 0; i < roomsData.length; i++) {
        const roomTags = roomsData[i].tag;
        const commonTags = roomTags.filter((x: any) => tags.includes(x));
        if (commonTags.length > 0) {
          contactBookRooms.push(roomsData[i].id);
        }
      }
    }
    if (contactBookRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 5000),
      );
      return;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      applyContactBookToRooms({
        contactBookId: id,
        property: user.property.id,
        rooms: contactBookRooms,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_room_calling", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function createCardText(contactBookId: string) {
    if (contactBooks.data === null) return null;
    const contactBookData = contactBooks.data.filter(
      (book) => book.id === contactBookId,
    )[0];

    // Get contact Ids for selected book
    const selectedContactIds = contactBookData.contacts.map(
      (x) => x.contact_id,
    );

    // Iterate through rooms and find matching contacts
    let count = 0;
    for (let i = 0; i < roomsData.length; i += 1) {
      const room = roomsData[i];
      const roomContactsRooms = room.contacts.rooms.map((x) => x.contact_id);
      let roomContactIds = [...roomContactsRooms];
      const roomContactNumbers = room.contacts.phone_numbers.map(
        (x) => x.contact_id,
      );
      roomContactIds = roomContactIds.concat(roomContactNumbers);
      const check = checkArraysContainSameElements(
        roomContactIds,
        selectedContactIds,
      );
      if (check) count += 1;
    }
    const text =
      count === 1
        ? translateText("room", language)
        : translateText("rooms", language);
    return `${count} ${text === undefined ? "" : text.toLowerCase()}`;
  }

  function selectOrUnselectAllRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      if (selectedContactBook !== null)
        setSelectedContactBook({
          ...selectedContactBook,
          rooms: allAvailableRooms,
        });
    } else {
      if (selectedContactBook !== null)
        setSelectedContactBook({ ...selectedContactBook, rooms: [] });
    }
  }

  function selectOrUnselectAllTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      if (selectedContactBook !== null)
        setSelectedContactBook({
          ...selectedContactBook,
          tags: allAvailableTags,
        });
    } else {
      if (selectedContactBook !== null)
        setSelectedContactBook({ ...selectedContactBook, tags: [] });
    }
  }

  function updateRoomForContactBook(roomId: string) {
    if (selectedContactBook === null) return;
    if (selectedContactBook.rooms.includes(roomId)) {
      const updatedRooms = selectedContactBook.rooms.filter(
        (x) => x !== roomId,
      );
      setSelectedContactBook({ ...selectedContactBook, rooms: updatedRooms });
    } else {
      const updatedRooms = [...selectedContactBook.rooms, roomId];
      setSelectedContactBook({ ...selectedContactBook, rooms: updatedRooms });
    }
  }

  function updateTagForContactBook(tag: string) {
    if (selectedContactBook === null) return;
    const existingTags = selectedContactBook.tags;
    if (existingTags.includes(tag)) {
      const updatedTags = existingTags.filter((x) => x !== tag);
      setSelectedContactBook({ ...selectedContactBook, tags: updatedTags });
    } else {
      const updatedTags = [...existingTags, tag];
      setSelectedContactBook({ ...selectedContactBook, tags: updatedTags });
    }
  }

  // Format rooms data
  const contactBooksData = contactBooks.data === null ? [] : contactBooks.data;
  const roomsData = rooms.data === null ? [] : rooms.data;
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("calling_title", language)} />

      {/* Apply contact book to rooms */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_contact_book", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditContactBook}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-black"
            >
              {`Edit contact book "${selectedContactBook?.name}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Choose rooms by */}
              <Dropdown
                id="choose_rooms_by"
                className="col-span-full"
                label={translateText("choose_rooms_by", language)}
                small={translateText(
                  "choose_rooms_by_contact_subtext",
                  language,
                )}
                showLabel={true}
                required={true}
                state={selectedContactBook}
                stateName="choose_rooms_by"
                onChange={setSelectedContactBook}
                defaultValue="name"
              >
                {chooseRoomsBy.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Rooms */}
              {selectedContactBook !== null &&
              selectedContactBook.choose_rooms_by === "name" ? (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_contact_book_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForContactBook}
                        checked={selectedContactBook.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("tag", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText(
                        "select_tags_for_rooms_announcement",
                        language,
                      )}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllTags}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomTags.map((tag: any) => (
                      <Checkbox
                        key={tag.id}
                        id={tag.id}
                        onChange={updateTagForContactBook}
                        checked={
                          selectedContactBook !== null
                            ? selectedContactBook.tags.includes(tag.id)
                            : false
                        }
                        label={tag.label}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {contactBooks.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {contactBooksData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {contactBooksData.map((book) => (
                    <a
                      href="#!"
                      key={book.id}
                      onClick={(e) => selectContactBookToEdit(e, [book.id])}
                    >
                      <Card
                        key={book.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {book.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className={`text-sm text-secondary`}>
                            {createCardText(book.id)}
                          </p>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_contact_books", language)}
                subtext={translateText("no_contact_books_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
